import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { isEmptyOrBlank } from "utils/utils";
import s from "./PayfacForm.module.scss";
import Spin from "components/Spin/Spin";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Selection from "components/Input/Selection/Selection";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import AgentStatusOptions from "utils/AgentStatusOptions/AgentStatusOptions";
import { emailFormatValidator, phoneFormatValidator } from "utils/formValidation";

const PAGE_LOCALE_KEY = "page.payfacs.addPayfac.";
const DIALOG_LOCALE_KEY = "page.dialogs.";
const CONFIG_GRID_STYLE = { marginBottom: 0 };

const ERROR_INIT_STATE = {
  name: false,
  firstName: false,
  lastName: false,
  email: false,
  address: false,
  phone: false,
  remarks: false,
  status: false,
  contactPerson: false,
  alipayPartnerKey: false,
  alipayPartnerId: false,
  wechatMerchantId: false,
  wechatAppId: false,
  wechatSecret: false,
  wechatKey: false,
  yedpayPayfacId: false,
  yedpayKid: false,
  yedpayPayfacHashId: false,
  payfacPrefix: false,
};

const PayfacForm = ({
  initialValues,
  onSubmit,
  loading,
  submitting,
  isEditMode = false,
  showActionDivider = true,
  gridId = "PayfacFormGrid",
}) => {
  const { t } = useTranslation();

  const [fieldInput, setFieldInput] = useState(initialValues);
  const [focus, setFocus] = useState({});
  const [error, setError] = useState(ERROR_INIT_STATE);

  useEffect(() => {
    setFieldInput(initialValues);
  }, [initialValues]);

  const statusOptions = AgentStatusOptions(
    ["ACTIVE", "SUSPENDED", "TERMINATED"],
    true,
  );

  const textInputOnChange = (e, field) => {
    setError({ ...error, [field]: false });
    const { value } = e.target;
    setFieldInput({ ...fieldInput, [field]: value });
  };

  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };

  const textInputOnBlur = (field, callback) => {
    if (callback) {
      if (fieldInput[field]) {
        const isValidValue = callback(fieldInput[field]);
        if (!isValidValue) {
          setError({ ...error, [field]: true });
        }
      } else {
        const isValidValue = callback("");
        if (!isValidValue) {
          setError({ ...error, [field]: true });
        }
      }
    }
    setFocus({ [field]: false });
  };

  const validateField = (field, value) => {
    if (isEmptyOrBlank(value)) return false;
    if (field === "email") return emailFormatValidator(value);
    if (field === "phone") return phoneFormatValidator(value);
    if (field === "payfacPrefix") return !isEmptyOrBlank(value);
    return true;
  };

  const handleSubmit = () => {
    const requiredField = ["name", "email", "phone"];
    if (isEditMode) {
      requiredField.push("contactPerson");
    } else {
      requiredField.push("firstName", "lastName", "payfacPrefix");
    }
    let hasError = false;
    requiredField.forEach((field) => {
      const fieldValue = fieldInput[field];
      if (!validateField(field, fieldValue)) {
        setError((error) => ({ ...error, [field]: true }));
        hasError = true;
      }
    });

    if (hasError) {
      return;
    }

    onSubmit(fieldInput);
  };

  if (loading) {
    return <Spin />;
  }

  return (
    <div className={s.contentContainer}>
      {submitting && <Spin type={"search"} />}
      <div className={s.content2}>
        <div className={s.itemContainer}>
          <Grid gridId={gridId}>
            <OutlinedText
              mandatory={true}
              label={t(PAGE_LOCALE_KEY + "detailColumns.name.label")}
              value={fieldInput.name}
              onchangeFn={(e) => textInputOnChange(e, "name")}
              onfocusFn={() => textInputOnFocus("name")}
              onblurFn={() => textInputOnBlur("name", (val) => validateField("name", val))}
              sxParams={{
                textFocus: focus.name,
                textError: error.name,
              }}
              errorMessage={t(
                PAGE_LOCALE_KEY + "detailColumns.name.errorInput",
              )}
            />
            {isEditMode ? (
              <OutlinedText
                mandatory={true}
                label={t(PAGE_LOCALE_KEY + "detailColumns.contactPerson.label")}
                value={fieldInput.contactPerson}
                onchangeFn={(e) => textInputOnChange(e, "contactPerson")}
                onfocusFn={() => textInputOnFocus("contactPerson")}
                onblurFn={() => textInputOnBlur("contactPerson", (val) => validateField("contactPerson", val))}
                sxParams={{
                  textFocus: focus.contactPerson,
                  textError: error.contactPerson,
                }}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "detailColumns.contactPerson.errorInput",
                )}
              />
            ) : (
              <>
                <OutlinedText
                  mandatory={true}
                  label={t(PAGE_LOCALE_KEY + "detailColumns.firstName.label")}
                  value={fieldInput.firstName}
                  onchangeFn={(e) => textInputOnChange(e, "firstName")}
                  onfocusFn={() => textInputOnFocus("firstName")}
                  onblurFn={() => textInputOnBlur("firstName", (val) => validateField("firstName", val))}
                  sxParams={{
                    textFocus: focus.firstName,
                    textError: error.firstName,
                  }}
                  errorMessage={t(
                    PAGE_LOCALE_KEY + "detailColumns.firstName.errorInput",
                  )}
                />
                <OutlinedText
                  mandatory={true}
                  label={t(PAGE_LOCALE_KEY + "detailColumns.lastName.label")}
                  value={fieldInput.lastName}
                  onchangeFn={(e) => textInputOnChange(e, "lastName")}
                  onfocusFn={() => textInputOnFocus("lastName")}
                  onblurFn={() => textInputOnBlur("lastName", (val) => validateField("lastName", val))}
                  sxParams={{
                    textFocus: focus.lastName,
                    textError: error.lastName,
                  }}
                  errorMessage={t(
                    PAGE_LOCALE_KEY + "detailColumns.lastName.errorInput",
                  )}
                />
              </>
            )}
            <OutlinedText
              mandatory={true}
              label={t("page.payfacs.payfacslist.columnTitle.email")}
              value={fieldInput.email}
              onchangeFn={(e) => textInputOnChange(e, "email")}
              onfocusFn={() => textInputOnFocus("email")}
              onblurFn={() => textInputOnBlur("email", (val) => validateField("email", val))}
              sxParams={{
                textFocus: focus.email,
                textError: error.email,
              }}
              errorMessage={t(
                PAGE_LOCALE_KEY + "detailColumns.email.errorInput",
              )}
            />
            <OutlinedText
              mandatory={true}
              label={t(PAGE_LOCALE_KEY + "detailColumns.phone.label")}
              value={fieldInput.phone}
              onchangeFn={(e) => textInputOnChange(e, "phone")}
              onfocusFn={() => textInputOnFocus("phone")}
              onblurFn={() => textInputOnBlur("phone", (val) => validateField("phone", val))}
              sxParams={{
                textFocus: focus.phone,
                textError: error.phone,
              }}
              errorMessage={t(
                PAGE_LOCALE_KEY + "detailColumns.phone.errorInput",
              )}
            />
            <OutlinedText
              mandatory={!isEditMode}
              disabled={isEditMode}
              label={t(PAGE_LOCALE_KEY + "detailColumns.payfacPrefix.label")}
              value={fieldInput.payfacPrefix}
              onchangeFn={(e) => textInputOnChange(e, "payfacPrefix")}
              onfocusFn={() => textInputOnFocus("payfacPrefix")}
              onblurFn={() => textInputOnBlur("payfacPrefix", (val) => isEditMode ? true : validateField("payfacPrefix", val))}
              sxParams={{
                textFocus: focus.payfacPrefix,
                textError: error.payfacPrefix,
              }}
              errorMessage={t(PAGE_LOCALE_KEY + "detailColumns.payfacPrefix.errorInput")}
            />
            <OutlinedText
              label={t(PAGE_LOCALE_KEY + "detailColumns.address.label")}
              value={fieldInput.address}
              onchangeFn={(e) => textInputOnChange(e, "address")}
              onfocusFn={() => textInputOnFocus("address")}
              onblurFn={() => textInputOnBlur("address")}
              sxParams={{
                textFocus: focus.address,
              }}
            />
            <OutlinedText
              label={t(PAGE_LOCALE_KEY + "detailColumns.remarks.label")}
              value={fieldInput.remarks}
              onchangeFn={(e) => textInputOnChange(e, "remarks")}
              onfocusFn={() => textInputOnFocus("remarks")}
              onblurFn={() => textInputOnBlur("remarks")}
              sxParams={{
                textFocus: focus.remarks,
              }}
            />
          </Grid>
          <div className={s.configDivider} />
          <div className={s.configSectionList}>
            <section className={s.configSection}>
              <div className={s.configSectionTitle}>
                {t(PAGE_LOCALE_KEY + "configSection.yedpay")}
              </div>
              <Grid gridId={gridId} style={CONFIG_GRID_STYLE}>
                <OutlinedText
                  label={t(
                    PAGE_LOCALE_KEY + "detailColumns.yedpayPayfacId.label",
                  )}
                  value={fieldInput.yedpayPayfacId}
                  onchangeFn={(e) => textInputOnChange(e, "yedpayPayfacId")}
                  onfocusFn={() => textInputOnFocus("yedpayPayfacId")}
                  onblurFn={() => textInputOnBlur("yedpayPayfacId")}
                  sxParams={{
                    textFocus: focus.yedpayPayfacId,
                  }}
                />
                <OutlinedText
                  label={t(PAGE_LOCALE_KEY + "detailColumns.yedpayKid.label")}
                  value={fieldInput.yedpayKid}
                  onchangeFn={(e) => textInputOnChange(e, "yedpayKid")}
                  onfocusFn={() => textInputOnFocus("yedpayKid")}
                  onblurFn={() => textInputOnBlur("yedpayKid")}
                  sxParams={{
                    textFocus: focus.yedpayKid,
                  }}
                />
                <OutlinedText
                  label={t(
                    PAGE_LOCALE_KEY + "detailColumns.yedpayPayfacHashId.label",
                  )}
                  value={fieldInput.yedpayPayfacHashId}
                  onchangeFn={(e) => textInputOnChange(e, "yedpayPayfacHashId")}
                  onfocusFn={() => textInputOnFocus("yedpayPayfacHashId")}
                  onblurFn={() => textInputOnBlur("yedpayPayfacHashId")}
                  sxParams={{
                    textFocus: focus.yedpayPayfacHashId,
                  }}
                />
              </Grid>
            </section>

            <section className={s.configSection}>
              <div className={s.configSectionTitle}>
                {t(PAGE_LOCALE_KEY + "configSection.alipay")}
              </div>
              <Grid gridId={gridId} style={CONFIG_GRID_STYLE}>
                <OutlinedText
                  label={t(
                    PAGE_LOCALE_KEY + "detailColumns.alipayPartnerKey.label",
                  )}
                  value={fieldInput.alipayPartnerKey}
                  onchangeFn={(e) => textInputOnChange(e, "alipayPartnerKey")}
                  onfocusFn={() => textInputOnFocus("alipayPartnerKey")}
                  onblurFn={() => textInputOnBlur("alipayPartnerKey")}
                  sxParams={{
                    textFocus: focus.alipayPartnerKey,
                  }}
                />
                <OutlinedText
                  label={t(
                    PAGE_LOCALE_KEY + "detailColumns.alipayPartnerId.label",
                  )}
                  value={fieldInput.alipayPartnerId}
                  onchangeFn={(e) => textInputOnChange(e, "alipayPartnerId")}
                  onfocusFn={() => textInputOnFocus("alipayPartnerId")}
                  onblurFn={() => textInputOnBlur("alipayPartnerId")}
                  sxParams={{
                    textFocus: focus.alipayPartnerId,
                  }}
                />
              </Grid>
            </section>

            <section className={s.configSection}>
              <div className={s.configSectionTitle}>
                {t(PAGE_LOCALE_KEY + "configSection.wechat")}
              </div>
              <Grid gridId={gridId} style={CONFIG_GRID_STYLE}>
                <OutlinedText
                  label={t(
                    PAGE_LOCALE_KEY + "detailColumns.wechatMerchantId.label",
                  )}
                  value={fieldInput.wechatMerchantId}
                  onchangeFn={(e) => textInputOnChange(e, "wechatMerchantId")}
                  onfocusFn={() => textInputOnFocus("wechatMerchantId")}
                  onblurFn={() => textInputOnBlur("wechatMerchantId")}
                  sxParams={{
                    textFocus: focus.wechatMerchantId,
                  }}
                />
                <OutlinedText
                  label={t(PAGE_LOCALE_KEY + "detailColumns.wechatAppId.label")}
                  value={fieldInput.wechatAppId}
                  onchangeFn={(e) => textInputOnChange(e, "wechatAppId")}
                  onfocusFn={() => textInputOnFocus("wechatAppId")}
                  onblurFn={() => textInputOnBlur("wechatAppId")}
                  sxParams={{
                    textFocus: focus.wechatAppId,
                  }}
                />
                <OutlinedText
                  label={t(PAGE_LOCALE_KEY + "detailColumns.wechatSecret.label")}
                  value={fieldInput.wechatSecret}
                  onchangeFn={(e) => textInputOnChange(e, "wechatSecret")}
                  onfocusFn={() => textInputOnFocus("wechatSecret")}
                  onblurFn={() => textInputOnBlur("wechatSecret")}
                  sxParams={{
                    textFocus: focus.wechatSecret,
                  }}
                />
                <OutlinedText
                  label={t(PAGE_LOCALE_KEY + "detailColumns.wechatKey.label")}
                  value={fieldInput.wechatKey}
                  onchangeFn={(e) => textInputOnChange(e, "wechatKey")}
                  onfocusFn={() => textInputOnFocus("wechatKey")}
                  onblurFn={() => textInputOnBlur("wechatKey")}
                  sxParams={{
                    textFocus: focus.wechatKey,
                  }}
                />
              </Grid>
            </section>
          </div>
          {!isEditMode ? (
            <Grid gridId={gridId}>
              <Selection
                mandatory={true}
                label={t(PAGE_LOCALE_KEY + "detailColumns.status.label")}
                selectionValue={fieldInput.status}
                onchangeFn={(e) => textInputOnChange(e, "status")}
                onfocusFn={() => textInputOnFocus("status")}
                onblurFn={() => textInputOnBlur("status")}
                options={statusOptions}
                sxParams={{
                  textFocus: focus.status,
                  textError: error.status,
                }}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "detailColumns.status.errorInput",
                )}
              />
            </Grid>
          ) : null}
        </div>
      </div>
      <div
        className={[
          s.dialogActionContainer,
          !showActionDivider && s.noActionDivider,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className={s.dialogButtonContainer}>
          <ButtonMAS overlayLoading
            loading={submitting}
            onClick={handleSubmit}
          >
            {isEditMode
              ? t(DIALOG_LOCALE_KEY + "btnUpdate")
              : t(PAGE_LOCALE_KEY + "btnCreate")}
          </ButtonMAS>
        </div>
      </div>
    </div>
  );
};

export default React.memo(PayfacForm);
